import { useState, useEffect, useRef } from 'preact/hooks';
import { useAuth } from '../lib/auth';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function LoginPage() {
  const { login, workspace, loading: authLoading } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const lastProcessedToken = useRef(null);

  // Start through the API so it creates and verifies the signed OAuth state.
  const slackOAuthUrl = `${API_URL}/oauth/slack/install`;

  useEffect(() => {
    // Wait for auth context to finish loading before processing token
    if (authLoading) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const token = params.get('token');
    const redirect = params.get('redirect');

    if (code && code !== lastProcessedToken.current) {
      // Preferred flow: one-time code minted by the worker (Slack Home button).
      lastProcessedToken.current = code;
      handleCodeLogin(code, redirect);
    } else if (token && token !== lastProcessedToken.current) {
      // Legacy flow: signed token from older cached Slack Home buttons.
      lastProcessedToken.current = token;
      handleTokenLogin(token, redirect);
    } else if (!code && !token) {
      // Nothing to process - clear any stale session data silently
      lastProcessedToken.current = null;
      localStorage.removeItem('sessionId');
      // Clear error params from URL if present
      if (window.location.search) {
        window.history.replaceState({}, '', '/login');
      }
    }
  }, [authLoading, workspace]);

  // Normalize the post-login destination to a safe in-app path.
  function safeRedirect(path) {
    if (!path || !path.startsWith('/') || path.startsWith('//')) return '/';
    return path;
  }

  async function handleCodeLogin(code, redirectPath = null) {
    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/v3/auth/exchange-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code })
      });

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error('Invalid response from server');
      }

      if (!response.ok || !data.sessionId) {
        throw new Error(data.error || 'Login failed');
      }

      // Land on the requested page (router reads the URL when the app mounts).
      window.history.replaceState({}, '', safeRedirect(redirectPath));
      login(data.sessionId);
    } catch (err) {
      console.error('Code login failed:', err);
      setError(err.message || 'Login failed. Please try again.');
      localStorage.removeItem('sessionId');
      window.history.replaceState({}, '', '/login');
      setTimeout(() => setError(null), 8000);
    } finally {
      setProcessing(false);
    }
  }

  async function handleTokenLogin(token, redirectPath = null) {
    setProcessing(true);
    setError(null);

    try {
      // Use workspace ID from context (derived from subdomain)
      const workspaceId = workspace?.id;
      if (!workspaceId) {
        throw new Error('Workspace not found. Please check the URL.');
      }

      const response = await fetch(`${API_URL}/v3/auth/slack-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, workspaceId })
      });

      // Safely parse JSON - handle empty or invalid responses
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store session - auth context will re-initialize and redirect.
      // Clear URL params first (prevents re-processing) and land on the
      // requested page so the router renders it once authenticated.
      window.history.replaceState({}, '', safeRedirect(redirectPath));

      // Call login which will set session and re-initialize auth
      login(data.sessionId);

    } catch (err) {
      console.error('Token login failed:', err);
      // Show the specific error message from the server, or a generic one
      const errorMessage = err.message || 'Login failed. Please try again.';
      setError(errorMessage);
      // Clear the token from URL and any stale session data
      localStorage.removeItem('sessionId');
      window.history.replaceState({}, '', '/login');
      // Auto-clear error after 8 seconds (longer for detailed messages)
      setTimeout(() => setError(null), 8000);
    } finally {
      setProcessing(false);
    }
  }

  if (processing) {
    return (
      <div class="login-page">
        <div class="login-card">
          <div class="login-logo">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 class="login-title">Signing you in...</h1>
          <div class="spinner" style={{ margin: '2rem auto' }} />
          <p class="login-subtitle">
            Please wait while we verify your credentials from Slack.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div class="login-page">
      <div class="login-card">
        <div class="login-logo">
          <img src="/unplug-logo.png" alt="Unplug" style="width: 64px; height: 64px; border-radius: 12px;" />
        </div>

        <h1 class="login-title">{workspace?.name || 'Unplug'}</h1>
        <p class="login-subtitle">
          Manage your team's time off requests with ease
        </p>

        {error && (
          <div class="alert alert-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <a href={slackOAuthUrl} class="slack-btn">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
          </svg>
          Add to Slack
        </a>

        <p style="margin-top: 1.5rem; font-size: 0.875rem; color: var(--gray-500); text-align: center;">
          Already using Unplug?<br />
          Open the app in Slack and click "Admin Dashboard" to sign in.
        </p>

        <p style="margin-top: 1rem; font-size: 0.75rem; color: var(--gray-400);">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
